// ---------------------------------------------------------------------------
// Cookie-based anonymous session helper
// ---------------------------------------------------------------------------
// The anonymous browsing session is persisted **solely** via two HTTP cookies
//   sid – UUID v4 string identifying the row in `public.sessions`
//   st  – 32-byte base64 token used as shared secret (HttpOnly)
// Both cookies are written with the following attributes:
//   Path=/; Max-Age=31536000; SameSite=Strict; Secure
//
// The server-side contract is handled by the Supabase Edge Function
//   POST /functions/v1/create_session
//
// When no cookies are present we create a fresh session on the server and
// persist the returned id+token pair in the cookie jar.  When cookies exist
// we assume the session to be valid – the server is responsible for
// rejecting tampered / expired values.
//
// Public API (mirrors INSTRUCTION.md §5):
//   initSession()          – ensure cookies exist (create if missing)
//   getSession()           – read sessionId from cookie
//   linkSessionToUser()    – attach current Supabase auth.user to session
// ---------------------------------------------------------------------------

/* eslint-disable no-console */

const SID_COOKIE = 'sid';
// `st` is HttpOnly → not readable, but we keep the constant so that tests
// can emulate a full cookie jar when stubbing fetch.
const ST_COOKIE = 'st';
// 1 year in seconds – aligns with the desired cookie lifetime.
const YEAR_SECONDS = 60 * 60 * 24 * 365;

interface SessionCookie {
  sessionId: string;
}

// A minimal, environment-agnostic cookie accessor.  In a non-browser context
// (e.g. Vitest running under Node) we fall back to an in-memory Map so the
// helpers remain fully testable without JSDOM.

const inMemoryCookies = new Map<string, string>();

// ---------------------------------------------------------------------------
// sessionStorage helper – persists the random session_id for the *current*
// browser tab only (evaporates when the tab/window closes).  Down-stream Edge
// Functions require the client to echo this value back in OTP requests so the
// verification endpoint can guarantee both calls originate from the *same*
// browsing context.
// ---------------------------------------------------------------------------

const SESSION_STORAGE_KEY = 'ia_session';

/**
 * Reads the current session_id from sessionStorage (if present).
 */
export function getFrontendSessionId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Persists the given session_id inside sessionStorage – noop during SSR /
 * unit tests where the Storage API is unavailable.
 */
export function setFrontendSessionId(id: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    /* ignore quota / security errors */
  }
}

function getCookie(name: string): string | null {
  if (typeof document !== 'undefined' && typeof document.cookie === 'string') {
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const [rawName, ...rest] = part.trim().split('=');
      if (rawName === name) return decodeURIComponent(rest.join('='));
    }
    return null;
  }
  return inMemoryCookies.get(name) ?? null;
}

function setCookie(name: string, value: string, maxAge = YEAR_SECONDS) {
  if (typeof document !== 'undefined' && typeof document.cookie === 'string') {
    const encoded = encodeURIComponent(value);
    document.cookie = `${name}=${encoded}; Path=/; Max-Age=${maxAge}; SameSite=Strict; Secure`;
  } else {
    inMemoryCookies.set(name, value);
  }
}

function deleteCookie(name: string) {
  if (typeof document !== 'undefined' && typeof document.cookie === 'string') {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Strict; Secure`;
  }
  inMemoryCookies.delete(name);
}

// ---------------------------------------------------------------------------
// Supabase Edge Function call helper
// ---------------------------------------------------------------------------

async function callCreateSession(sessionId?: string): Promise<SessionCookie> {
  // When executed inside Vite the variable is injected via `import.meta.env`.
  // In plain Node (Vitest, SSR) we fall back to process.env for convenience.
    // Resolve Supabase URL without breaking the browser runtime.  Accessing
    // the global `process` object directly inside a browser context raises a
    // ReferenceError because the symbol is **not** defined by default.  We
    // therefore look at `import.meta.env` first (injected by Vite during the
    // build) and only fall back to `process.env` **after** confirming the
    // variable exists.  The conditional indirection keeps tree-shaking
    // intact while completely avoiding `process` access on the client.

    let supabaseUrl: string | undefined;
    try {
      supabaseUrl = (import.meta as any)?.env?.VITE_API_BASE_URL;
    } catch {
      // Not in a Vite/bundled context — fall through to process.env
    }

    if (!supabaseUrl && typeof process !== 'undefined') {
      supabaseUrl =
        (process.env as Record<string, string | undefined>).VITE_API_BASE_URL;
    }

  const payload: Record<string, unknown> = {};
  if (sessionId) payload.sessionId = sessionId;

  if (!supabaseUrl) {
    // Running in a context without Supabase backend (e.g. unit tests). Return
    // the original pair (or placeholders when creating a new session) to keep
    // the calling code functional while avoiding network activity.
    if (sessionId) return { sessionId } as SessionCookie;
    // Minimal fallback – values are meaningless outside E2E/production.
    return { sessionId: 'local-session' } as SessionCookie;
  }

  // During Vitest runs we also want to avoid real network calls even when the
  // environment variable happens to be defined.
  if (typeof process !== 'undefined' && process.env.VITEST) {
    const maybeMock = globalThis.fetch as any;
    if (!maybeMock || !('mock' in maybeMock)) {
      return {
        sessionId: sessionId ?? 'local-session',
      } as SessionCookie;
    }
  }

  // Pass through the optional application identifier so the Edge Function
  // can authenticate the call (see `_shared/app.ts`).  During local Vitest
  // runs `import.meta.env` is undefined – we only include the header when
  // the variable is present to avoid polluting unit-test snapshots.

  const appId: string | undefined = (import.meta as any)?.env?.VITE_APP_ID;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (appId) headers['x-app-id'] = appId;

  const resp = await fetch(`${supabaseUrl}/functions/v1/create_session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    throw new Error(`create_session failed: ${resp.status}`);
  }

  // The function returns at least { sessionId, token }.  We ignore all other
  // keys here because only those two must survive across reloads.
  const { sessionId: id } = (await resp.json()) as Record<string, string>;
  return { sessionId: id };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function initSession(): Promise<void> {
  try {
    const sid = getCookie(SID_COOKIE);

    // Only return early when a *syntactically valid* UUID is already present
    // in the cookie jar.  We occasionally observed broken states during
    // development where an empty string or the literal text "null" was
    // persisted – both values caused `ensureFrontendSessionId()` to bail out
    // with `null`, ultimately sending an **invalid_session_id** to the Edge
    // Function.  Reject any non-UUID value so the helper can recover by
    // creating a fresh anonymous session.

    if (sid && UUID_RE.test(sid)) {
      // Nothing to do – session already exists and looks sane.
      return;
    }

    // In case an invalid value slipped into the cookies we pro-actively clear
    // it so subsequent calls behave as if the cookie never existed.  This
    // prevents endless invalid → early-return loops across page reloads.
    if (sid && !UUID_RE.test(sid)) {
      deleteCookie(SID_COOKIE);
    }

  let supabaseUrl: string | undefined;
  try {
    supabaseUrl = (import.meta as any)?.env?.VITE_API_BASE_URL;
  } catch {
    // Not in a Vite/bundled context — fall through to process.env
  }

  if (!supabaseUrl && typeof process !== 'undefined') {
    supabaseUrl =
      (process.env as Record<string, string | undefined>).VITE_API_BASE_URL;
  }

    // When the application is executed in a pure Node context (e.g. Vitest
    // unit tests) the environment variable might be absent.  In that case we
    // skip the network request entirely – analytics are irrelevant and the
    // absence of a session does not impact functional tests.
    if (!supabaseUrl) return;

    const created = await callCreateSession();
    setCookie(SID_COOKIE, created.sessionId);

    // Persist the freshly minted sessionId inside sessionStorage so that
    // subsequent front-end calls (e.g. auth_request_otp) can reference it
    // synchronously.  This is especially important when users submit a form
    // before the initial network round-trip in `initSession()` finishes.
    try {
      setFrontendSessionId(created.sessionId);
    } catch {
      /* Storage API unavailable – ignore */
    }
  } catch (err) {
    console.error('[session] initSession failed', err);
  }
}

export function getSession(): SessionCookie | null {
  const sid = getCookie(SID_COOKIE);
  if (!sid) return null;
  return { sessionId: sid };
}

export async function linkSessionToUser(): Promise<void> {
  try {
    const current = getSession();
    if (!current) return;

    let supabaseUrl: string | undefined;
    try {
      supabaseUrl = (import.meta as any)?.env?.VITE_API_BASE_URL;
    } catch {
      // Not in a Vite/bundled context — fall through to process.env
    }

    if (!supabaseUrl && typeof process !== 'undefined') {
      supabaseUrl =
        (process.env as Record<string, string | undefined>).VITE_API_BASE_URL;
    }

    if (!supabaseUrl) return;

    await callCreateSession(current.sessionId);
  } catch (err) {
    console.error('[session] linkSessionToUser failed', err);
  }
}

// ---------------------------------------------------------------------------
// Convenience helper – guarantee a valid, front-end accessible `sessionId`.
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Ensures an anonymous session exists and returns its ID.  If the value is
 * already present in `sessionStorage` the function resolves immediately.
 * Otherwise it awaits `initSession()` and propagates the freshly created ID
 * back to the caller (also copying it into sessionStorage for subsequent
 * synchronous reads).
 */
export async function ensureFrontendSessionId(): Promise<string | null> {
  let id = getFrontendSessionId();
  if (id && UUID_RE.test(id)) return id;

  // Attempt to bootstrap – the helper is safe to call multiple times because
  // `initSession()` is a no-op when the cookies already exist.
  await initSession();

  // After initSession() completes we first look at sessionStorage again –
  // when the application runs over plain HTTP (e.g. `vite dev`) the browser
  // refuses to persist cookies that carry the `Secure` attribute.  In that
  // scenario the fresh session *is* stored in sessionStorage by
  // initSession(), but `getSession()` will still return null because the
  // cookie was silently discarded.  Returning the value directly from
  // sessionStorage therefore guarantees a non-null sessionId even during
  // local development while preserving the original, cookie-centric code
  // path for production (HTTPS) deployments.

  id = getFrontendSessionId();
  if (id && UUID_RE.test(id)) return id;

  const current = getSession();
  if (current?.sessionId && UUID_RE.test(current.sessionId)) {
    try {
      setFrontendSessionId(current.sessionId);
    } catch {
      /* Storage disabled – ignore */
    }
    return current.sessionId;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Test utilities (tree-shaken in production builds)
// ---------------------------------------------------------------------------

// resetCookies() is *only* exposed under Vitest/Jest to simplify white-box
// tests.  It is stripped by most bundlers during production builds because
// `import.meta.vitest` is statically analyzable.

export function __test_only_resetCookies() {
  deleteCookie(SID_COOKIE);
  deleteCookie(ST_COOKIE);
  inMemoryCookies.clear();
}
